import type {
  AssistantContextInput,
  AssistantContextKind,
  AssistantFocus,
} from "@cloudserver/contracts";
import { kindOf, terminals } from "../terminal/store.js";

/*
 * What the panel tells the assistant about the page the person is on
 * (docs/assistant.md): the project, the resource the page shows, the object
 * in focus and the words for suggested first questions. In the session
 * workspace the focused tab is the page: its server and its project (the
 * console's own address forwards there, and the chat once took that for «Вся
 * организация»).
 */

export interface PageInfo {
  path: string;
  /** The project of the page; null outside projects («Вся организация»). */
  projectId: string | null;
  /** The resource the page shows; its label is filled in when known. */
  resource: { kind: AssistantContextKind; resourceId: string; collection: string } | null;
  /** What the page is about, for suggestions. */
  topic: "server" | "costs" | "firewall" | "network" | "balancer" | "other";
}

/** The focused session tab of the workspace, as the chat needs it. */
export interface SessionTab {
  projectId: string;
  serverId: string;
  serverName: string;
  kind: "ssh" | "vnc" | "rdp";
  unixUser?: string | undefined;
}

const COLLECTIONS: Record<string, AssistantContextKind> = {
  servers: "server",
  networks: "network",
  firewalls: "firewall",
  "load-balancers": "load_balancer",
};

/** Page paths that are not resources although they look like one. */
const NOT_IDS = new Set(["new"]);

/** The session workspace and the console's address, which forwards to it. */
const SESSION_PAGES = /^\/terminals\/?$|^\/projects\/[^/]+\/servers\/[^/]+\/console\/?$/;

export function pageOf(path: string, tab: SessionTab | null = null): PageInfo {
  if (tab && SESSION_PAGES.test(path))
    return {
      path,
      projectId: tab.projectId,
      resource: { kind: "server", resourceId: tab.serverId, collection: "servers" },
      topic: "server",
    };
  const match = /^\/projects\/([0-9a-f-]{36})(?:\/([a-z-]+)(?:\/([^/]+))?)?/.exec(path);
  const projectId = match?.[1] ?? null;
  const collection = match?.[2] ?? "";
  const id = match?.[3];
  const kind = COLLECTIONS[collection];
  const resource = kind && id && !NOT_IDS.has(id) ? { kind, resourceId: id, collection } : null;
  const topic: PageInfo["topic"] =
    collection === "servers"
      ? "server"
      : collection === "costs" || path === "/costs"
        ? "costs"
        : collection === "firewalls"
          ? "firewall"
          : collection === "networks"
            ? "network"
            : collection === "load-balancers"
              ? "balancer"
              : "other";
  return { path, projectId, resource, topic };
}

/** The chip of the page's resource, once its name is known. */
export function chipOf(page: PageInfo, label: string): AssistantContextInput | null {
  return page.resource
    ? { kind: page.resource.kind, resourceId: page.resource.resourceId, label }
    : null;
}

const SESSION_KINDS = { ssh: "terminal", vnc: "console", rdp: "desktop" } as const;

/**
 * The object in focus (docs/assistant.md, "Layers"): the focused session tab
 * while the workspace is on screen, else the page's resource.
 */
export function focusOf(input: {
  tab: SessionTab | null;
  resource: { kind: AssistantContextKind; resourceId: string; label: string } | null;
}): AssistantFocus | null {
  if (input.tab)
    return {
      kind: "session",
      resourceKind: "server",
      resourceId: input.tab.serverId,
      label: input.tab.serverName,
      session: SESSION_KINDS[input.tab.kind],
      ...(input.tab.kind === "ssh" && input.tab.unixUser ? { unixUser: input.tab.unixUser } : {}),
    };
  if (input.resource)
    return {
      kind: "resource",
      resourceKind: input.resource.kind,
      resourceId: input.resource.resourceId,
      label: input.resource.label,
    };
  return null;
}

/**
 * What the chat does with a focused tab of another project than the
 * conversation's: never mixes projects — it offers the conversation about
 * that server, or a new one.
 */
export function otherProject(
  tab: SessionTab | null,
  conversationProject: string | null,
): SessionTab | null {
  return tab && conversationProject !== null && tab.projectId !== conversationProject ? tab : null;
}

/**
 * The focused session tab of the workspace, whatever its state (the
 * workspace's page is "about" it); `visibleOnly` only while it is on screen
 * and connected — the focus that follows the active tab.
 */
export function focusedTab(visibleOnly = true): SessionTab | null {
  const state = terminals.snapshot();
  const tab = state.tabs.find((t) => t.id === state.active);
  if (!tab) return null;
  if (visibleOnly && (!state.visible || tab.status !== "connected")) return null;
  return {
    projectId: tab.projectId,
    serverId: tab.serverId,
    serverName: tab.serverName,
    kind: kindOf(tab),
    ...(kindOf(tab) === "ssh" && tab.unixUser ? { unixUser: tab.unixUser } : {}),
  };
}
